import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControlLabel,
  Tooltip,
  Switch
} from '@mui/material';
import {
  ArrowBack,
  AttachMoney,
  AccountBalance,
  Add,
  Visibility,
  Send,
  LocalAtm,
  Star,
  Schedule,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import TransactionLedger from './TransactionLedger';
import BankOfferingsManager from './BankOfferingsManager';
import PlayerBankAccounts from './PlayerBankAccounts';
import TransferDialog from './TransferDialog';
import EntityCreationWarning from './EntityCreationWarning';
import { getApiUrl } from '../config';
import {
  GlassBackground,
  GlassContainer,
  GlassCard,
  GlassButton,
  GlassTextField,
  GlassIconButton,
  GlassInfoCard,
  GlassDialog,
  GlassChip,
  GlassShimmer,
  useGlassShimmer
} from './ui/GlassComponents';
import { GlassPageHeader } from './ui/GlassLayoutComponents';

const EconomicManagement = ({ authData, sessionData, onBack }) => {
  const shimmerRef = useGlassShimmer('.glass-card, .glass-container');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [currencies, setCurrencies] = useState([]);
  const [inflationIndex, setInflationIndex] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [userMemberships, setUserMemberships] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [nations, setNations] = useState([]);
  
  // Dialog states
  const [createCurrencyDialog, setCreateCurrencyDialog] = useState(false);
  const [createBankDialog, setCreateBankDialog] = useState(false);
  const [bankDetailsDialog, setBankDetailsDialog] = useState(false);
  const [addOfficerDialog, setAddOfficerDialog] = useState(false);
  const [removeOfficerDialog, setRemoveOfficerDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transactionLedgerDialog, setTransactionLedgerDialog] = useState(false);
  const [viewAllAccountsDialog, setViewAllAccountsDialog] = useState(false);
  
  // Transfer dialog state
  const [transferSource, setTransferSource] = useState(null);
  
  // Transaction ledger state
  const [ledgerEntity, setLedgerEntity] = useState({
    type: '', // 'treasury' or 'bank'
    id: '',
    name: '',
  });
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [accountsFilters, setAccountsFilters] = useState({
    activeOnly: false,
    limit: 100,
    offset: 0,
  });
  
  // Form states
  const [newCurrency, setNewCurrency] = useState({
    name: '',
    issuingNation: '',
  });
  const [newBank, setNewBank] = useState({
    name: '',
    jurisdictionNation: '',
  });
  const [newOfficer, setNewOfficer] = useState('');
  const [officerToRemove, setOfficerToRemove] = useState('');

  // Fetch functions
  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      setCurrencies(data.currencies || []);
    } catch (err) {
      setError('Failed to load currencies: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchInflationIndex = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/currencies/inflation-index'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch inflation index');
      }
      
      const data = await response.json();
      setInflationIndex(data);
    } catch (err) {
      console.warn('Failed to load inflation index:', err.message);
      // Don't show error to user for this non-critical feature
    }
  }, [sessionData.token]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/banks'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch banks');
      }
      
      const data = await response.json();
      setBanks(data.banks || []);
    } catch (err) {
      setError('Failed to load banks: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchUserMemberships = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/nations/my-memberships'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user memberships');
      }
      
      const data = await response.json();
      setUserMemberships(data.memberships || []);
    } catch (err) {
      setError('Failed to load user memberships: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchNations = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/nations'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch nations');
      }
      
      const data = await response.json();
      setNations(data.nations || []);
    } catch (err) {
      setError('Failed to load nations: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchBankDetails = async (bankId) => {
    try {
      const response = await fetch(getApiUrl(`/banks/${bankId}`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch bank details');
      }
      
      const data = await response.json();
      setSelectedBank(data);
      setBankDetailsDialog(true);
    } catch (err) {
      setError('Failed to load bank details: ' + err.message);
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchInflationIndex();
    fetchBanks();
    fetchUserMemberships();
    fetchNations();
  }, [fetchCurrencies, fetchInflationIndex, fetchBanks, fetchUserMemberships, fetchNations]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleCreateCurrency = async () => {
    if (!newCurrency.name || !newCurrency.issuingNation) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/currencies'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          ...newCurrency,
          createdBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create currency');
      }

      setCreateCurrencyDialog(false);
      setNewCurrency({ name: '', issuingNation: '' });
      fetchCurrencies();
      fetchInflationIndex();
      fetchUserMemberships();
    } catch (err) {
      setError('Failed to create currency: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBank = async () => {
    if (!newBank.name || !newBank.jurisdictionNation) {
      setError('Please fill in all required fields');
      return;
    }

    const bankId = generateBankId(newBank.name);
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/banks'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          bankId,
          name: newBank.name,
          jurisdictionNation: newBank.jurisdictionNation,
          createdBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create bank');
      }

      setCreateBankDialog(false);
      setNewBank({
        name: '',
        jurisdictionNation: '',
      });
      await fetchBanks();
      setError(''); // Clear any existing error
      setSuccess('Bank created successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess(''); // Clear any existing success message
      setError('Failed to create bank: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, symbol) => {
    return `${symbol}${amount.toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Get nations where user is an officer and nation has no currency
  const getValidNationsForCurrency = () => {
    return userMemberships
      .filter(membership => membership.isOfficer && !membership.nationalCurrency)
      .map(membership => membership.nationName);
  };

  // Generate bank ID from bank name (first letters of each word)
  const generateBankId = (bankName) => {
    return bankName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 4); // Limit to 4 characters max
  };

  // Get nations where user is a citizen or officer (for bank jurisdiction)
  const getValidNationsForBank = () => {
    return userMemberships.map(membership => membership.nationName);
  };

  // Check if user is an officer of the selected bank
  const isUserBankOfficer = (bank) => {
    if (!bank || !sessionData?.selectedDisplayName) return false;
    
    if (!bank.officers || !Array.isArray(bank.officers)) return false;
    
    // Check if officers array contains objects (detailed bank data) or strings (list bank data)
    if (bank.officers.length > 0) {
      const firstOfficer = bank.officers[0];
      
      // For detailed bank data (from bank details API) - officers are objects with name property
      if (typeof firstOfficer === 'object' && firstOfficer.name) {
        return bank.officers.some(officer => officer.name === sessionData.selectedDisplayName);
      }
      
      // For list bank data (from banks list API) - officers are strings
      if (typeof firstOfficer === 'string') {
        return bank.officers.includes(sessionData.selectedDisplayName);
      }
    }
    
    return false;
  };

  // Handle adding bank officer
  const handleAddBankOfficer = async () => {
    if (!newOfficer || !selectedBank) {
      setError('Please enter an officer name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${selectedBank.id}/officers`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          officerName: newOfficer,
          addedBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add officer');
      }

      setAddOfficerDialog(false);
      setNewOfficer('');
      await fetchBankDetails(selectedBank.id); // Refresh bank details
      setError('');
      setSuccess('Officer added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to add officer: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle removing bank officer
  const handleRemoveBankOfficer = async () => {
    if (!officerToRemove || !selectedBank) {
      setError('Invalid officer selection');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${selectedBank.id}/officers/${officerToRemove}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          removedBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove officer');
      }

      setRemoveOfficerDialog(false);
      setOfficerToRemove('');
      await fetchBankDetails(selectedBank.id); // Refresh bank details
      setError('');
      setSuccess('Officer removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to remove officer: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle bank transfer success
  const handleTransferSuccess = async (result) => {
    // Refresh bank details if we have a selected bank
    if (selectedBank) {
      await fetchBankDetails(selectedBank.id);
    }
    setError('');
    setSuccess('Transfer completed successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const openTransactionLedger = (type, id, name) => {
    setLedgerEntity({ type, id, name });
    setTransactionLedgerDialog(true);
  };

  const fetchBankAccounts = async (bankId) => {
    setAccountsLoading(true);
    setAccountsError('');
    try {
      const params = new URLSearchParams({
        limit: accountsFilters.limit.toString(),
        offset: accountsFilters.offset.toString(),
        ...(accountsFilters.activeOnly && { active_only: 'true' }),
      });

      const response = await fetch(getApiUrl(`/banks/${bankId}/accounts?${params}`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch bank accounts');
      }

      const data = await response.json();
      setBankAccounts(data.accounts || []);
    } catch (err) {
      setAccountsError('Failed to load bank accounts: ' + err.message);
      setBankAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleViewAllAccounts = () => {
    if (selectedBank) {
      fetchBankAccounts(selectedBank.id);
      setViewAllAccountsDialog(true);
    }
  };

  // Helper function to get inflation data for a currency
  const getInflationDataForCurrency = (currencySymbol) => {
    if (!inflationIndex || !inflationIndex.currencies) return null;
    return inflationIndex.currencies.find(c => c.symbol === currencySymbol);
  };

  // Helper function to get inflation risk color
  const getInflationRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const renderCurrenciesTab = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }} gutterBottom>Currencies</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          View all currencies in the world and their economic indicators
        </Typography>
      </Box>

      {currencies.length === 0 ? (
        <GlassInfoCard severity="info">
          No currencies found. Create currencies through national treasuries!
        </GlassInfoCard>
      ) : (
        <Grid container spacing={3}>
          {currencies.map((currency) => {
            const inflationData = getInflationDataForCurrency(currency.symbol);
            return (
              <Grid item xs={12} md={6} lg={4} key={currency.symbol}>
                <GlassCard className="glass-card" sx={{ height: '100%' }}>
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AttachMoney sx={{ mr: 1, color: 'rgba(255, 193, 7, 0.9)' }} />
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>{currency.name}</Typography>
                      <GlassChip
                        variant="info"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        ${currency.symbol}
                      </GlassChip>
                    </Box>

                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                      <strong>Issuing Nation:</strong> {currency.issuingNation}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                      <strong>Current Supply:</strong> {formatCurrency(currency.currentSupply, `$${currency.symbol}`)}
                    </Typography>

                    {inflationData && (
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <GlassChip
                          size="small"
                          variant={inflationData.purchasingPowerIndex >= 100 ? 'success' : 'warning'}
                          title={`Purchasing Power Parity Index: ${inflationData.purchasingPowerIndex.toFixed(1)} (100 = baseline)`}
                        >
                          PPP: {inflationData.purchasingPowerIndex.toFixed(0)}
                        </GlassChip>
                        <GlassChip
                          size="small"
                          variant={getInflationRiskColor(inflationData.inflationRisk)}
                          title={`Inflation Risk Level based on supply per capita`}
                        >
                          Risk: {inflationData.inflationRisk.toUpperCase()}
                        </GlassChip>
                        {inflationData.supplyPerCapita && (
                          <GlassChip
                            size="small"
                            variant="default"
                            title={`Supply per capita: ${inflationData.supplyPerCapita.toFixed(2)}`}
                          >
                            {inflationData.supplyPerCapita.toFixed(0)}/capita
                          </GlassChip>
                        )}
                      </Box>
                    )}
                  </Box>
                </GlassCard>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );

  const renderBanksTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>All Banks</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Discover banks and their offerings to open new accounts
          </Typography>
        </Box>
        <GlassButton
          variant="glass"
          onClick={() => setCreateBankDialog(true)}
        >
          <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
          Create Bank
        </GlassButton>
      </Box>

      {banks.length === 0 ? (
        <GlassInfoCard severity="info">
          No banks found. Create the first bank!
        </GlassInfoCard>
      ) : (
        <Grid container spacing={3}>
          {banks.map((bank) => (
            <Grid item xs={12} md={6} lg={4} key={bank.id}>
              <GlassCard
                className="glass-card"
                clickable
                onClick={() => fetchBankDetails(bank.id)}
                sx={{ height: '100%' }}
              >
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountBalance sx={{ mr: 1, color: 'rgba(102, 126, 234, 0.9)' }} />
                    <Typography variant="h6" sx={{ flexGrow: 1, color: 'white', fontWeight: 600 }}>{bank.name}</Typography>
                    {isUserBankOfficer(bank) && (
                      <Tooltip title="You are an officer of this bank">
                        <Star sx={{ color: 'rgba(255, 193, 7, 0.9)', fontSize: 20 }} />
                      </Tooltip>
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                    <strong>Abbreviation:</strong> {bank.abbreviation}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                    <strong>Jurisdiction:</strong> {bank.jurisdictionNation}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                    <strong>Active Accounts:</strong> {bank.accountCount}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                    <strong>Officers:</strong> {bank.officerCount}
                  </Typography>

                  {/* Banking Feature Chips */}
                  <Box sx={{ mt: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {/* National Currency Chip */}
                    {bank.nationalCurrency ? (
                      <GlassChip
                        size="small"
                        variant="success"
                      >
                        Currency: {bank.nationalCurrency}
                      </GlassChip>
                    ) : (
                      <GlassChip
                        size="small"
                        variant="error"
                      >
                        No National Currency
                      </GlassChip>
                    )}

                    {/* Interest Payment Success Rate */}
                    {bank.nationalCurrency && (
                      <GlassChip
                        size="small"
                        variant={
                          (bank.interestSuccessRate || 0) >= 95 ? 'success' :
                          (bank.interestSuccessRate || 0) >= 75 ? 'warning' : 'error'
                        }
                      >
                        Interest: {bank.interestSuccessRate || 0}% success
                      </GlassChip>
                    )}

                    {/* Account Offerings Summary */}
                    {bank.offeringSummary && (
                      <>
                        {bank.offeringSummary.hasInterestAccounts && (
                          <GlassChip
                            size="small"
                            variant="info"
                          >
                            Savings Available
                          </GlassChip>
                        )}
                        {bank.offeringSummary.hasOverdraft && (
                          <GlassChip
                            size="small"
                            variant="warning"
                          >
                            Overdraft Available
                          </GlassChip>
                        )}
                        {bank.offeringSummary.lowestMaintenanceFee !== null && (
                          <GlassChip
                            size="small"
                            variant="default"
                          >
                            Min Fee: {bank.offeringSummary.lowestMaintenanceFee}
                          </GlassChip>
                        )}
                      </>
                    )}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <GlassButton
                      variant="glass"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchBankDetails(bank.id);
                      }}
                      fullWidth
                    >
                      <Visibility sx={{ fontSize: '1rem', mr: 0.5 }} />
                      View Offerings
                    </GlassButton>
                  </Box>
                </Box>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderMarketsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>Markets</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            View and manage markets
          </Typography>
        </Box>
      </Box>

      {renderCurrenciesTab()}
    </Box>
  );

  return (
    <GlassBackground>
      <GlassShimmer ref={shimmerRef} />
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <GlassContainer className="glass-container">
          <GlassPageHeader
            title="Banking & Finance"
            action={
              <GlassIconButton onClick={onBack}>
                <ArrowBack />
              </GlassIconButton>
            }
          />

          {error && (
            <GlassInfoCard severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
              {error}
            </GlassInfoCard>
          )}

          {success && (
            <GlassInfoCard severity="success" onClose={() => setSuccess('')} sx={{ mb: 3 }}>
              {success}
            </GlassInfoCard>
          )}

          {/* Custom Tab Switcher */}
          <Box sx={{
            position: 'relative',
            display: 'flex',
            width: '100%',
            maxWidth: '600px',
            height: '56px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            mb: 4,
            mx: 'auto'
          }}>
            {/* Sliding background indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: '4px',
                left: tabValue === 0 ? '4px' : tabValue === 1 ? 'calc(33.33% + 2px)' : 'calc(66.66% + 2px)',
                height: 'calc(100% - 8px)',
                width: 'calc(33.33% - 6px)',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 1,
              }}
            />

            <Box
              onClick={() => handleTabChange(null, 0)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '33.33%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'color 0.3s ease',
                color: tabValue === 0 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                gap: 0.5
              }}
            >
              <AccountBalance sx={{ fontSize: '1.2rem' }} />
              <span>My Accounts</span>
            </Box>
            <Box
              onClick={() => handleTabChange(null, 1)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '33.33%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'color 0.3s ease',
                color: tabValue === 1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                gap: 0.5
              }}
            >
              <Visibility sx={{ fontSize: '1.2rem' }} />
              <span>All Banks</span>
            </Box>
            <Box
              onClick={() => handleTabChange(null, 2)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '33.33%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'color 0.3s ease',
                color: tabValue === 2 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                gap: 0.5
              }}
            >
              <AttachMoney sx={{ fontSize: '1.2rem' }} />
              <span>Markets</span>
            </Box>
          </Box>

        {tabValue === 0 && (
          <Box>
            <PlayerBankAccounts sessionData={sessionData} />
          </Box>
        )}
        {tabValue === 1 && renderBanksTab()}
        {tabValue === 2 && renderMarketsTab()}

        {/* Create Currency Dialog */}
        <GlassDialog
          open={createCurrencyDialog}
          onClose={() => setCreateCurrencyDialog(false)}
          title="Create New Currency"
          maxWidth="sm"
          actions={
            <>
              <GlassButton variant="glass" onClick={() => setCreateCurrencyDialog(false)}>
                Cancel
              </GlassButton>
              <GlassButton variant="glass" onClick={handleCreateCurrency} disabled={loading}>
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create'}
              </GlassButton>
            </>
          }
        >
          <GlassInfoCard severity="info">
            The currency symbol will be automatically generated from your nation's abbreviation (e.g., US → $US).
          </GlassInfoCard>
          <GlassTextField
            fullWidth
            label="Currency Name"
            value={newCurrency.name}
            onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., US Dollar, Euro, Yen"
          />
          <FormControl fullWidth margin="normal" required
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.95)' },
              '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
            }}
          >
            <InputLabel>Issuing Nation</InputLabel>
            <Select
              value={newCurrency.issuingNation}
              onChange={(e) => setNewCurrency({ ...newCurrency, issuingNation: e.target.value })}
              label="Issuing Nation"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                      },
                    },
                  },
                },
              }}
            >
              {getValidNationsForCurrency().map((nationName) => (
                <MenuItem key={nationName} value={nationName}>
                  {nationName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </GlassDialog>

        {/* Create Bank Dialog */}
        <GlassDialog
          open={createBankDialog}
          onClose={() => setCreateBankDialog(false)}
          title="Create Bank"
          maxWidth="md"
          actions={
            <>
              <GlassButton variant="glass" onClick={() => setCreateBankDialog(false)}>
                Cancel
              </GlassButton>
              <GlassButton variant="glass" onClick={handleCreateBank} disabled={loading}>
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create'}
              </GlassButton>
            </>
          }
        >
          <EntityCreationWarning
            entityType="bank"
            additionalWarning="Wait! Are you looking for an account? This menu is to create a bank. Most people do not operate banks!"
          />
          <GlassTextField
            fullWidth
            label="Bank Name"
            value={newBank.name}
            onChange={(e) => setNewBank({ ...newBank, name: e.target.value })}
            margin="normal"
            required
            helperText="Bank ID will be auto-generated from the name (e.g., 'Purple National Bank' → 'PNB')"
          />
          <FormControl fullWidth margin="normal" required
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.95)' },
              '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
            }}
          >
            <InputLabel>Jurisdiction Nation</InputLabel>
            <Select
              value={newBank.jurisdictionNation}
              onChange={(e) => setNewBank({ ...newBank, jurisdictionNation: e.target.value })}
              label="Jurisdiction Nation"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                      },
                    },
                  },
                },
              }}
            >
              {getValidNationsForBank().map((nationName) => (
                <MenuItem key={nationName} value={nationName}>
                  {nationName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {newBank.name && (
            <GlassInfoCard severity="info" sx={{ mt: 2 }}>
              Bank ID will be: <strong>{generateBankId(newBank.name)}</strong>
            </GlassInfoCard>
          )}
        </GlassDialog>

        {/* Bank Details Dialog */}
        {selectedBank && (
          <GlassDialog
            open={bankDetailsDialog}
            onClose={() => setBankDetailsDialog(false)}
            maxWidth="md"
            fullWidth
            title={`${selectedBank.name} (${selectedBank.abbreviation})`}
            actions={
              <GlassButton variant="glass" onClick={() => setBankDetailsDialog(false)}>Close</GlassButton>
            }
          >
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }} gutterBottom>
                  <strong>Jurisdiction:</strong> {selectedBank.jurisdictionNation}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }} gutterBottom>
                  <strong>Created:</strong> {formatDate(selectedBank.createdAt)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Accounts:</strong> {selectedBank.accountCount}
                </Typography>
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Officers Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: 2
                }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>Officers</Typography>
                  {isUserBankOfficer(selectedBank) && (
                    <GlassButton
                      variant="glass"
                      size="small"
                      onClick={() => setAddOfficerDialog(true)}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
                      Add Officer
                    </GlassButton>
                  )}
                </Box>
                
                {selectedBank.officers && selectedBank.officers.length > 0 ? (
                  <TableContainer component={GlassCard}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Appointed</TableCell>
                          {isUserBankOfficer(selectedBank) && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBank.officers.map((officer) => (
                          <TableRow key={officer.name}>
                            <TableCell>{officer.name}</TableCell>
                            <TableCell>{formatDate(officer.appointedAt)}</TableCell>
                            {isUserBankOfficer(selectedBank) && (
                              <TableCell align="center">
                                {officer.name !== sessionData.selectedDisplayName && (
                                  <GlassButton
                                    variant="glass"
                                    size="small"
                                    onClick={() => {
                                      setOfficerToRemove(officer.name);
                                      setRemoveOfficerDialog(true);
                                    }}
                                    sx={{
                                      color: 'rgba(255, 107, 107, 0.9)',
                                      border: '1px solid rgba(255, 107, 107, 0.3)',
                                      '&:hover': {
                                        background: 'rgba(255, 107, 107, 0.15)',
                                      }
                                    }}
                                  >
                                    Remove
                                  </GlassButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <GlassInfoCard severity="info">
                    No officers found
                  </GlassInfoCard>
                )}
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Bank Balances Section - Only visible to bank officers */}
              {isUserBankOfficer(selectedBank) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>Bank Balances</Typography>
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 2
                  }}>
                    <GlassButton
                      variant="glass"
                      size="small"
                      onClick={() => {
                        setTransferSource(selectedBank);
                        setTransferDialog(true);
                      }}
                      sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                    >
                      <Send sx={{ fontSize: '1rem', mr: 0.5 }} />
                      Transfer
                    </GlassButton>
                    <GlassButton
                      variant="glass"
                      size="small"
                      onClick={() => openTransactionLedger('bank', selectedBank.id, selectedBank.name)}
                      sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                    >
                      <LocalAtm sx={{ fontSize: '1rem', mr: 0.5 }} />
                      History
                    </GlassButton>
                    <GlassButton
                      variant="glass"
                      size="small"
                      onClick={handleViewAllAccounts}
                      sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                    >
                      <Schedule sx={{ fontSize: '1rem', mr: 0.5 }} />
                      All Accounts
                    </GlassButton>
                  </Box>
                  
                  {selectedBank.balances && selectedBank.balances.length > 0 ? (
                    <TableContainer component={GlassCard}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Currency</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Balance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedBank.balances.map((balance) => (
                            <TableRow key={balance.currencySymbol}>
                              <TableCell>{balance.currencyName}</TableCell>
                              <TableCell>{balance.currencySymbol}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(balance.balance, balance.currencySymbol)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <GlassInfoCard severity="info">
                      No currency balances. Banks receive funds through transfers from treasuries or other banks.
                    </GlassInfoCard>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Bank Offerings Section */}
              <Box sx={{ mb: 3 }}>
                <BankOfferingsManager
                  bankId={selectedBank.id}
                  bankName={selectedBank.name}
                  sessionData={sessionData}
                  isOfficer={isUserBankOfficer(selectedBank)}
                />
              </Box>
          </GlassDialog>
        )}

        {/* Add Officer Dialog */}
        <GlassDialog
          open={addOfficerDialog}
          onClose={() => setAddOfficerDialog(false)}
          title="Add Bank Officer"
          maxWidth="sm"
          actions={
            <>
              <GlassButton variant="glass" onClick={() => setAddOfficerDialog(false)}>
                Cancel
              </GlassButton>
              <GlassButton variant="glass" onClick={handleAddBankOfficer} disabled={!newOfficer}>
                Add Officer
              </GlassButton>
            </>
          }
        >
          <GlassTextField
            fullWidth
            label="Officer Name"
            value={newOfficer}
            onChange={(e) => setNewOfficer(e.target.value)}
            margin="normal"
            required
            helperText="Enter the display name of the player to add as an officer"
          />
        </GlassDialog>

        {/* Remove Officer Dialog */}
        <GlassDialog
          open={removeOfficerDialog}
          onClose={() => setRemoveOfficerDialog(false)}
          title="Remove Bank Officer"
          maxWidth="sm"
          actions={
            <>
              <GlassButton variant="glass" onClick={() => setRemoveOfficerDialog(false)}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="glass"
                onClick={handleRemoveBankOfficer}
                sx={{ color: 'rgba(255, 107, 107, 0.9)' }}
              >
                Remove Officer
              </GlassButton>
            </>
          }
        >
          <GlassInfoCard severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{officerToRemove}</strong> as an officer of {selectedBank?.name}?
          </GlassInfoCard>
        </GlassDialog>

        {/* Transfer Dialog */}
        <TransferDialog
          open={transferDialog}
          onClose={() => {
            setTransferDialog(false);
            setTransferSource(null);
          }}
          onTransfer={handleTransferSuccess}
          availableCurrencies={currencies}
          userDisplayName={sessionData?.selectedDisplayName}
          sessionData={sessionData}
          prefillFromType={transferSource ? "bank" : null}
          prefillFromId={transferSource?.id || null}
          prefillFromName={transferSource ? `${transferSource.name} Vault` : null}
        />

        {/* Transaction Ledger Dialog */}
      <TransactionLedger
        open={transactionLedgerDialog}
        onClose={() => setTransactionLedgerDialog(false)}
        entityType={ledgerEntity.type}
        entityId={ledgerEntity.id}
        entityName={ledgerEntity.name}
        sessionData={sessionData}
      />

      {/* View All Accounts Dialog */}
      <GlassDialog
        open={viewAllAccountsDialog}
        onClose={() => setViewAllAccountsDialog(false)}
        maxWidth="md"
        fullWidth
        title={`All Accounts for ${selectedBank?.name || ''}`}
        actions={
          <GlassButton variant="glass" onClick={() => setViewAllAccountsDialog(false)}>Close</GlassButton>
        }
      >
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
             <FormControlLabel
               control={
                 <Switch
                   checked={accountsFilters.activeOnly}
                   onChange={(e) => {
                     setAccountsFilters(prev => ({ ...prev, activeOnly: e.target.checked }));
                     if (selectedBank) {
                       fetchBankAccounts(selectedBank.id);
                     }
                   }}
                 />
               }
               label="Show only accounts with balances"
               sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
             />
             <GlassButton
               variant="glass"
               size="small"
               onClick={() => selectedBank && fetchBankAccounts(selectedBank.id)}
               disabled={accountsLoading}
             >
               <RefreshIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
               Refresh
             </GlassButton>
           </Box>
           
           {accountsLoading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
               <CircularProgress />
             </Box>
           ) : accountsError ? (
             <GlassInfoCard severity="error" sx={{ mb: 2 }}>{accountsError}</GlassInfoCard>
           ) : bankAccounts.length === 0 ? (
             <GlassInfoCard severity="info">
               {accountsFilters.activeOnly ?
                 'No accounts with balances found for this bank.' :
                 'No accounts found for this bank.'}
             </GlassInfoCard>
           ) : (
                         <TableContainer component={GlassCard}>
               <Table size="small">
                 <TableHead>
                   <TableRow>
                     <TableCell>Account Number</TableCell>
                     <TableCell>Account Holder</TableCell>
                     <TableCell>Offering</TableCell>
                     <TableCell>Balances</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Created</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {bankAccounts.map((account) => (
                     <TableRow key={account.accountNumber}>
                       <TableCell>{account.accountNumber}</TableCell>
                       <TableCell>{account.accountHolder}</TableCell>
                       <TableCell>{account.offering || 'Basic Account'}</TableCell>
                       <TableCell>
                         {account.balances && account.balances.length > 0 ? (
                           <Box>
                             {account.balances.map((balance) => (
                               <Typography key={balance.currencySymbol} variant="caption" display="block">
                                 {formatCurrency(balance.balance, balance.currencySymbol)}
                               </Typography>
                             ))}
                           </Box>
                         ) : (
                           <Typography variant="caption" color="text.secondary">
                             No balances
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         <GlassChip
                           size="small"
                           variant={account.hasBalances ? 'success' : 'default'}
                         >
                           {account.hasBalances ? 'Active' : 'Empty'}
                         </GlassChip>
                         {account.isDefault && (
                           <GlassChip
                             size="small"
                             variant="default"
                             sx={{ ml: 1 }}
                           >
                             Default
                           </GlassChip>
                         )}
                       </TableCell>
                       <TableCell>{formatDate(account.createdAt * 1000)}</TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             </TableContainer>
          )}
      </GlassDialog>
        </GlassContainer>
      </Container>
    </GlassBackground>
  );
};

export default EconomicManagement; 